import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Grid,
  Divider,
  FormControlLabel,
  Checkbox,
  InputAdornment,
} from "@mui/material";
import {
  Close as CloseIcon,
  Hotel as HotelIcon,
  SquareFoot as SquareFootIcon,
  AttachMoney as MoneyIcon,
  Save as SaveIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { parseResponseJSON } from "./CommonFunctions";

export const ManageRoomInfo = (props) => {
  const [showRoomDetailsPopup, setShowRoomDetailsPopup] = useState(false);
  const [roomId, setRoomId] = useState("");
  const [bedType, setBedType] = useState("");
  const [size, setSize] = useState("");
  const [weeklyRate, setWeeklyRate] = useState("");
  const [dailyRate, setDailyRate] = useState("");
  const [rentableCheckBox, setRentableCheckBox] = useState(false);
  const [alias, setAlias] = useState("");
  
  const url = process.env.REACT_APP_SERVER_URL;

  useEffect(() => {
    setShowRoomDetailsPopup(true);
    if (props.value) {
      setRoomId(props.value);
      getRoomAndAmenityDetailsById(props.value);
    } else {
      setRoomId(null);
    }
  }, []);

  const handleCloseShowRoomDetailsPopup = () => {
    setShowRoomDetailsPopup(false);
    setRoomId(null);
    if (props && props.setShowRoomDetailsPopup) {
      props.setShowRoomDetailsPopup(false);
    }
  };

  const getRoomAndAmenityDetailsById = async (roomId) => {
    try {
      const response = await axios.get(`${url}/rooms/allAmenities/${roomId}`);
      setStateValues(response.data[0]);
    } catch (error) {
      console.log(error);
    }
  };

  const createNewRoom = async (e) => {
    if (e) e.preventDefault();
    try {
      const available_status = 0;
      const rentable = rentableCheckBox ? 1 : 0;
      await axios.post(`${url}/rooms`, {
        bedType,
        size,
        available_status,
        weeklyRate,
        dailyRate,
        alias,
        rentable,
      });
      props.getRoomsDetails();
      handleCloseShowRoomDetailsPopup();
    } catch (error) {
      console.log(error);
    }
  };

  const editRoomDetails = async (e, roomId) => {
    if (e) e.preventDefault();
    try {
      const rentable = rentableCheckBox ? 1 : 0;
      await axios.patch(`${url}/rooms/${roomId}`, {
        bedType,
        size,
        weeklyRate,
        dailyRate,
        alias,
        rentable,
      });
      props.getRoomsDetails();
      handleCloseShowRoomDetailsPopup();
    } catch (error) {
      console.log(error);
    }
  };

  const setStateValues = (roomDetails) => {
    try {
      if (roomDetails) {
        setRoomId(roomDetails.id);
        setBedType(roomDetails.bedType);
        setSize(roomDetails.size);
        setWeeklyRate(roomDetails.weeklyRate);
        setDailyRate(roomDetails.dailyRate);
        setAlias(roomDetails.alias);
        setRentableCheckBox(roomDetails.rentable == 1);
      } else {
        setRoomId();
        setBedType();
        setSize();
        setWeeklyRate();
        setDailyRate();
        setAlias();
        setRentableCheckBox(false);
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Dialog
      open={showRoomDetailsPopup}
      onClose={handleCloseShowRoomDetailsPopup}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 1.5,
          px: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <HotelIcon sx={{ fontSize: 22 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {roomId ? `Edit Room #${roomId}` : "Add New Room"}
          </Typography>
        </Box>
        <IconButton
          onClick={handleCloseShowRoomDetailsPopup}
          size="small"
          sx={{
            color: "white",
            "&:hover": {
              background: "rgba(255,255,255,0.2)",
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider />

      {/* Content */}
      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Box component="form">
          <Grid container spacing={2}>
            {/* Room Name */}
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", mb: 0.5, display: "block" }}>
                ROOM NAME
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={alias}
                onChange={(e) => setAlias(e.target.value)}
                placeholder="e.g., Ocean View, Suite 101"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <HotelIcon sx={{ color: "primary.main", fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Bed Type */}
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", mb: 0.5, display: "block" }}>
                BED TYPE
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={bedType}
                  onChange={(e) => setBedType(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    Select bed type
                  </MenuItem>
                  <MenuItem value="Single">Single</MenuItem>
                  <MenuItem value="Double">Double</MenuItem>
                  <MenuItem value="Queen">Queen</MenuItem>
                  <MenuItem value="King">King</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Room Size */}
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", mb: 0.5, display: "block" }}>
                ROOM SIZE
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={size}
                  onChange={(e) => setSize(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    Select size
                  </MenuItem>
                  <MenuItem value="Small">Small</MenuItem>
                  <MenuItem value="Medium">Medium</MenuItem>
                  <MenuItem value="Large">Large</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Weekly Rate */}
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", mb: 0.5, display: "block" }}>
                WEEKLY RATE
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="number"
                value={weeklyRate}
                onChange={(e) => setWeeklyRate(e.target.value)}
                placeholder="0.00"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MoneyIcon sx={{ color: "success.main", fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Daily Rate */}
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", mb: 0.5, display: "block" }}>
                DAILY RATE
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="number"
                value={dailyRate}
                onChange={(e) => setDailyRate(e.target.value)}
                placeholder="0.00"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MoneyIcon sx={{ color: "success.main", fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Do Not Rent Checkbox */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rentableCheckBox}
                    onChange={(e) => setRentableCheckBox(!rentableCheckBox)}
                    size="small"
                    sx={{
                      color: "error.main",
                      "&.Mui-checked": {
                        color: "error.main",
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body" sx={{ fontWeight: 600, color: "error.main" }}>
                    Do Not Rent
                  </Typography>
                }
                sx={{
                  mt: 0.5,
                  p: 1,
                  borderRadius: 1,
                  border: rentableCheckBox ? "1px solid" : "1px solid transparent",
                  borderColor: rentableCheckBox ? "error.main" : "transparent",
                  background: rentableCheckBox ? "rgba(244, 67, 54, 0.05)" : "transparent",
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <Divider />

      {/* Footer */}
      <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1 }}>
        <Button
          onClick={handleCloseShowRoomDetailsPopup}
          variant="outlined"
          size="small"
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Cancel
        </Button>

        {roomId != null ? (
          <Button
            onClick={(e) => editRoomDetails(e, roomId)}
            variant="contained"
            size="small"
            startIcon={<SaveIcon fontSize="small" />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
              },
            }}
          >
            Save Changes
          </Button>
        ) : (
          <Button
            onClick={(e) => createNewRoom(e)}
            variant="contained"
            size="small"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
              },
            }}
          >
            Add Room
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};